import isEmpty from "lodash/isEmpty";
import queryString from "query-string";
import { useEffect, useReducer, useRef } from "react";
import { Link } from "react-router-dom";
import { RouterProps } from "@/components/shared/withRouting";

import Alert from "@/components/shared/Alert";
import Footer from "@/components/shared/Footer";
import PasswordInput from "@/components/shared/PasswordInput";
import TextInput from "@/components/shared/TextInput";
import { useLogOutUserMutation } from "@/query";
import {
  SET_ENFORCED_TWO_FACTOR,
  SET_SESSION_DATA
} from "@/redux/auth/sessions/actions";
import { SessionManager } from "@/services/SessionManager";
import { Team, User } from "@/types";
import { VendorUtilities, apiFetchVendorV1 } from "@/utilities/VendorUtilities";
import { setJWTCookie } from "@/utils/jwt-cookie";
import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { Button } from "../shared/Button";
import ExternalLink from "../shared/ExternalLink";
import ReplicatedInfo from "./ReplicatedInfo";

interface AcceptInviteProps {
  getInvite: (id: string) => void;
  handleGoogleInviteAccept: (id: string, replaceAccount: boolean) => void;
  hasToken?: string;
  invite: {
    createdAt: string;
    email: string;
    hasConflict: boolean;
    id: string;
  };
  team: Team;
  user: User;
}

interface AcceptInviteState {
  firstName?: string;
  lastName?: string;
  password?: string;
  email?: string;
  errors?: {
    validateError: boolean;
    firstName?: {
      error: string;
    };
    lastName?: {
      error: string;
    };
    password?: {
      error: string;
    };
    server?: {
      error: string;
    };
  };
  confirmPassword?: string;
  activationCodeSubmitted?: boolean;
  activationCode?: string;
}

const AcceptInvite = ({
  getInvite,
  handleGoogleInviteAccept,
  hasToken,
  invite,
  team,
  user,
  navigate,
  location,
}: AcceptInviteProps & RouterProps) => {
  const [state, setState] = useReducer(
    (prevState: AcceptInviteState, newState: AcceptInviteState) => ({
      ...prevState,
      ...newState
    }),
    {
      firstName: "",
      lastName: "",
      password: "",
      email: "",
      confirmPassword: "",
      errors: null,
      activationCodeSubmitted: false,
      activationCode: ""
    }
  );
  const firstNameRef = useRef(null);
  const { mutate: logOutUser } = useLogOutUserMutation();
  const dispatch = useDispatch();

  const {
    mutate: handleAcceptInvite,
    isLoading: acceptInviteLoading,
    error: acceptInviteError
  } = useMutation({
    mutationFn: async (payload: {
      invite_id: string;
      first_name: string;
      last_name: string;
      password: string;
      replace_account: boolean;
      from_team_selection: boolean;
    }) => {
      const path = "/signup/accept-invite";
      return (
        await apiFetchVendorV1(path, {
          endpoint: SessionManager.getApiEndpoint(),
          method: "POST",
          body: JSON.stringify(payload),
          includeAuth: user?.has_google_authed
        })
      ).json();
    },
    onSuccess: data => {
      if (data.samlRedirect) {
        logOutUser(true);
        navigate(data.samlRedirect);
        return;
      }
      dispatch({ type: SET_SESSION_DATA, payload: data });
      // Keep cookie in sync with Redux so downstream (ex: Cloudflare worker) has access.
      // NOTE: This cookie is JS-readable; see `src/utils/jwt-cookie.ts` for details.
      setJWTCookie(data?.token);
      SessionManager.setSessionId();

      if (!data.user["2fa_enabled"] && data.user["2fa_enforced"]) {
        dispatch({ type: SET_ENFORCED_TWO_FACTOR, payload: true });
        navigate("/require-2fa");
        return;
      }

      // Successful invite acceptance should take the user into the vendor portal.
      // Preserve any explicit next= param (used by some invite/login flows).
      const nextUrl = new URLSearchParams(location.search).get("next");
      const nextPath = nextUrl ? nextUrl.replace(/^\/+/, "") : "apps";
      navigate(`/${nextPath}`);
    },
    onError: async (error: Error) => {
      const err = JSON.parse(error?.message);
      setState({
        errors: err
      });
    },
    retry: false
  });

  const handleFormChange = (name: string, value: string) => {
    setState({
      [name]: value
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setState({
      errors: null
    });

    if (state.password.trim() !== state.confirmPassword.trim()) {
      setState({
        errors: {
          password: {
            error: "Passwords must match"
          },
          validateError: true
        }
      });
      return;
    }

    const parsedSearch = queryString.parse(location.search);
    const inviteSecret = location.hash.slice(1);

    let replaceAccount = false;
    if (parsedSearch.replace_account) {
      replaceAccount = parsedSearch.replace_account === "1";
    }

    let inviteFromTeamSelection = false;
    if (parsedSearch.invite_from_team_selection) {
      inviteFromTeamSelection = parsedSearch.invite_from_team_selection === "1";
    }

    const payload = {
      invite_id: inviteSecret,
      first_name: state.firstName.trim(),
      last_name: state.lastName.trim(),
      password: state.password.trim(),
      replace_account: replaceAccount,
      from_team_selection: inviteFromTeamSelection
    };

    handleAcceptInvite(payload);
  };

  const setInviteState = () => {
    setState({
      email: invite.email
    });
  };

  const handleGoogleSignupClick = ev => {
    ev.preventDefault();

    const isInvite = !isEmpty(invite);
    if (isInvite) {
      const parsedSearch = queryString.parse(location.search);
      let replaceAccount = false;
      if (parsedSearch.replace_account) {
        replaceAccount = parsedSearch.replace_account === "1";
      }

      handleGoogleInviteAccept(invite.id, replaceAccount);
    }
  };

  useEffect(() => {
    if (invite && team) {
      setInviteState();
    }
  }, [invite, team]);

  useEffect(() => {
    VendorUtilities.purgeVOneData();

    // if user is logged in, redirect to the next page
    if (!!hasToken && user?.id) {
      const urlParameters = new URLSearchParams(location.search);
      const nextUrl = urlParameters.get("next");
      navigate(`/${nextUrl || "apps"}`);
      return;
    }

    const inviteSecret = location.hash.slice(1);
    if (inviteSecret) {
      getInvite(inviteSecret);
    } else {
      navigate("/signup");
    }

    if (isEmpty(acceptInviteError)) {
      if (firstNameRef.current) {
        firstNameRef.current.focus();
      }
    }
  }, []);

  // if a user is already logged in
  // we return nothing while we wait for our useEffect to redirect them
  if (!!hasToken && user?.id) return null;

  const isInvite = !isEmpty(invite) || (location.hash.slice(1) ? true : false);

  // if invite hasn't been set yet, show nothing until it loads
  if (!invite || !team) return null;

  return (
      <div className="h-full w-full flex flex-col overflow-y-auto">
      <div className="flex flex-col-reverse shrink-0 min-[800px]:flex-row flex-auto w-full">
        <ReplicatedInfo needsActivation={false} />
        <div className="bg-page-bg lg:ml-0 lg:text-left flex-1 min-h-0 flex flex-col items-center justify-center pb-3 text-left auth-pages">
          <div className="w-full max-w-[400px] mx-auto max-[430px]:w-[90%] lg:mx-0 !mt-8 md:mt-0">
            <h1 className="text-3xl text-indigo-500 leading-[1.4] text-center">
              Join {team.name}'s team
            </h1>
          </div>
          <div className="w-full max-w-[400px] mx-auto max-[430px]:w-[90%] lg:mx-0">
            <div className="flex mt-[15px] mb-5">
              <a
                href="#"
                onClick={handleGoogleSignupClick}
                className="py-[10px] px-[25px] w-full text-base font-semibold rounded-[7px] border-2 border-solid border-hex-4285f4 text-hex-4285f4 bg-white hover:bg-hex-f3f7ff !flex items-center justify-center"
              >
                <span className="icon clickable google-g mr-3" />{" "}
                Continue with Google
              </a>
            </div>
            {!team?.is_google_auth_required && (
              <>
                <div className="flex items-center justify-center gap-4">
                  <div className="h-px w-[100px] bg-gray-410" />
                  <span className="text-sm font-semibold leading-none text-gray-410">or</span>
                  <div className="h-px w-[100px] bg-gray-410" />
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="FormSection-wrapper auth-pages !pb-2 flex flex-col gap-4">
                    {state.errors?.server ? (
                      <Alert variant="error" className="!text-pink-600 mb-1.5">
                        {state.errors.server?.error}
                      </Alert>
                    ) : null}
                    {state.errors?.firstName ? (
                      <Alert variant="error" className="!text-pink-600 mb-1.5">
                        {state.errors.firstName?.error}
                      </Alert>
                    ) : null}
                    {state.errors?.lastName ? (
                      <Alert variant="error" className="!text-pink-600 mb-1.5">
                        {state.errors.lastName?.error}
                      </Alert>
                    ) : null}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="firstName">First Name</label>
                        <TextInput
                          className={
                            state.errors?.firstName && "!border-pink-400"
                          }
                          type="text"
                          placeholder="Jack"
                          ref={firstNameRef}
                          size={20}
                          value={state.firstName}
                          name="firstName"
                          onChangeValue={value =>
                            handleFormChange("firstName", value)
                          }
                        />
                      </div>
                      <div>
                        <label htmlFor="lastName">Last Name</label>
                        <TextInput
                          className={
                            state.errors?.lastName && "!border-pink-400"
                          }
                          type="text"
                          placeholder="Shephard"
                          size={20}
                          value={state.lastName}
                          name="lastName"
                          onChangeValue={value =>
                            handleFormChange("lastName", value)
                          }
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="companyEmail">Company Email</label>
                      <TextInput
                        id="companyEmail"
                        type="text"
                        placeholder="jack@example.com"
                        value={state.email}
                        disabled={isInvite}
                        name="email"
                      />
                    </div>
                    {state.errors?.password ? (
                      <Alert variant="error" className="!text-pink-600 mb-1.5">
                        {state.errors.password?.error}
                      </Alert>
                    ) : null}
                    <div>
                      <label htmlFor="password">Password</label>
                      <PasswordInput
                        name="password"
                        className="Input"
                        placeholder="Password"
                        autoComplete="new-password"
                        hasError={!!state.errors?.password}
                        value={state.password}
                        onChangeValue={value => setState({ password: value })}
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="confirmPassword">Confirm Password</label>
                      <PasswordInput
                        name="confirmPassword"
                        className="Input"
                        placeholder="Confirm password"
                        autoComplete="new-password"
                        hasError={!!state.errors?.password}
                        value={state.confirmPassword}
                        onChangeValue={value =>
                          setState({ confirmPassword: value })
                        }
                      />
                    </div>
                  </div>
                  {!state.errors && acceptInviteError?.message && (
                    <Alert variant="error" className="!mt-0 text-pink-600 text-base">
                      {acceptInviteError.message}
                    </Alert>
                  )}
                  <div className="FormButton-wrapper flex">
                    <Button
                      kind="primary-purple"
                      size="large"
                      type="submit"
                      className="flex-none mt-2.5 mb-5 w-full !text-lg"
                      disabled={acceptInviteLoading}
                    >
                      {acceptInviteLoading ? "Creating" : "Create account"}
                    </Button>
                  </div>
                  <div className="flex-none ml-3 text-sm items-center justify-center flex flex-col">
                    <p className="text-gray-410 text-center">
                      Have an account?{" "}
                      <Link
                        to="/login"
                        className="text-hex-2e96c2 hover:underline hover:cursor-pointer font-medium"
                      >
                        Log in
                      </Link>
                    </p>
                  </div>
                  <div className="flex-none ml-3 text-sm items-center justify-center flex flex-col">
                    <p className="text-gray-410 text-center pt-5">
                      By clicking "Get Started", I agree to Replicated's{" "}
                      <ExternalLink
                        className="text-hex-2e96c2 hover:underline hover:cursor-pointer font-medium"
                        href="https://replicated.com/terms"
                      >
                        TOS
                      </ExternalLink>{" "}
                      and{" "}
                      <ExternalLink
                        className="text-hex-2e96c2 hover:underline hover:cursor-pointer font-medium"
                        href="https://replicated.com/privacy"
                      >
                        Privacy Policy
                      </ExternalLink>
                      .
                    </p>
                  </div>
                </form>
              </>
            )}
          </div>
          <div className="absolute bottom-[10px] right-[10px] !text-gray-200 max-lg:mx-auto max-lg:pt-[10px] max-lg:static max-lg:w-full max-lg:text-center max-lg:min-h-[100px]">
            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
